import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import CallEndIcon from '@mui/icons-material/CallEnd';
import { useAPI } from '@components/APIProvider';
import { AudioVisualizer } from '@/web_voice/frontend/AudioVisualizer';
import { IdleAudioVisualizer } from '@/web_voice/frontend/IdleAudioVisualizer';
import LiveConversationHistory from '@/conversations/frontend/LiveConversationHistory';
import { useTwilioVoice } from './useTwilioVoice';
import './VoiceChatWidgetInternal.css';
import LocalTranslatedText from '@/translation/frontend/components/LocalTranslatedText';

export default function VoiceChatWidgetInternal() {
  const api = useAPI();
  const [voiceConfiguration, setVoiceConfiguration] = useState(null);

  useEffect(() => {
    const loadVoiceConfiguration = async () => {
      const config = await api.getVoiceConfiguration();
      setVoiceConfiguration(config);
    };
    loadVoiceConfiguration();
  }, [api]);

  const {
    status,
    error,
    audioStream,
    isMuted,
    canConnect,
    canDisconnect,
    canToggleMute,
    handleConnect,
    handleDisconnect,
    handleToggleMute,
    conversation,
  } = useTwilioVoice(voiceConfiguration);

  if (!voiceConfiguration) {
    return (
      <div>
        <LocalTranslatedText text="Loading voice configuration..." />
      </div>
    ); // or a spinner
  }

  return (
    <Box
      className="web-voice-widget-internal"
      sx={{
        padding: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" spacing={2}>
        <Stack
          direction="column"
          spacing={2}
          className="web-voice-widget-internal-controls"
        >
          <Typography variant="h6" gutterBottom>
            Voice Agent Connection
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}

          <Typography variant="body1">
            Status: {status}
            {[
              'Initializing...',
              'Fetching token...',
              'Initializing device...',
              'Connecting...',
            ].includes(status) && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Typography>

          <Stack direction="column" spacing={2}>
            <Button
              variant="contained"
              color="success"
              startIcon={<MicIcon />}
              onClick={handleConnect}
              disabled={!canConnect}
            >
              Connect
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={isMuted ? <MicOffIcon /> : <MicIcon />}
              onClick={handleToggleMute}
              disabled={!canToggleMute}
            >
              {isMuted ? 'Unmute' : 'Mute'}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<CallEndIcon />}
              onClick={handleDisconnect}
              disabled={!canDisconnect}
            >
              Disconnect
            </Button>
          </Stack>

          {audioStream ? (
            <AudioVisualizer
              stream={audioStream}
              voiceConfiguration={voiceConfiguration}
              shape={
                voiceConfiguration?.web_voice_widget_configuration
                  ?.chat_audio_visualizer_shape || 'circle'
              }
            />
          ) : (
            <IdleAudioVisualizer />
          )}
        </Stack>

        {conversation && (
          <div
            id="twilio-web-rtc-conversation-history"
            className="conversation-history-view-wrapper"
          >
            <LiveConversationHistory
              conversation={conversation}
              scrollElementQuerySelector="#twilio-web-rtc-conversation-history"
            />
          </div>
        )}
      </Stack>
    </Box>
  );
}
